import {LocaleContextValue, useLocale as usePlatformLocale} from 'platform/locale';

import {has, isNotNil} from 'ramda';

import {languageDialogItems} from '../consts/languageDialogItems';
import {FALLBACK_LANGUAGE} from '../i18n/fallbackLanguage';
import {i18n} from '../i18n/i18n';
import {AppLanguage} from '../types/AppLanguage';

export type DcLocale = Omit<LocaleContextValue, 'language'> & {language: AppLanguage};

const isAppLanguage = (value: string | undefined): value is AppLanguage =>
  isNotNil(value) && has(value, languageDialogItems);

/**
 * Digital certificate override of the platform `useLocale`.
 *
 * The platform `LocaleProvider` maps languages it does not know (nl, sv, ro)
 * to `en`, so `useLocale().language` cannot be used to resolve audit label
 * translations or the PDF language. This hook keeps the platform locale
 * config (date/number formatting) but returns the application language
 * resolved by i18next, which supports all digital certificate languages.
 */
export function useLocale(): DcLocale {
  const locale = usePlatformLocale();
  const resolvedLanguage = i18n?.resolvedLanguage;
  const language: AppLanguage = isAppLanguage(resolvedLanguage)
    ? resolvedLanguage
    : FALLBACK_LANGUAGE;

  return {...locale, language};
}
